import axios, { type AxiosResponse } from 'axios';

export interface VehicleImage {
    make: string;
    model: string;
    year: number;
    /** small unless otherwise stated */
    size?: 'small' | 'medium' | 'large';
    /** angled-front unless otherwise stated */
    angle?: 'angled-front' | 'dashboard' | 'front' | 'side' | 'back' | 'cargo-empty' | 'cargo-stuff';
}

const getIMG2StockPhotoData = async (vehicleImage: VehicleImage): Promise<string | null> => {
    const requestUrl = getIMG2StockPhotoUrl(vehicleImage);
    return axios({
        method: 'get',
        url: requestUrl,
        responseType: 'blob',
    })
        .then((result: AxiosResponse<Blob>) => {
            // Check to see that the requested URL matches the final (possibly redirected) response URL. This will catch a redirect to the "coming soon" image.
            if (requestUrl == result.request?.responseURL) {
                return window.URL.createObjectURL(result.data);
            } else {
                // "Coming Soon" redirect -> we don't want an image.
                return null;
            }
        })
        .catch(() => {
            return null;
        });
};

const getIMG2StockPhotoUrl = (vehicleImage: VehicleImage): string => {
    let size: string = '?width=320';
    switch (vehicleImage.size) {
        case 'medium':
            size = '?width=640';
            break;
        case 'large':
            size = '?width=1024';
            break;
        case 'small':
        default:
            break;
    }

    return `https://img2.carmax.com/assets/mmy-${encodeURIComponent(
        vehicleImage.make
    )}-${encodeURIComponent(vehicleImage.model)}-${encodeURIComponent(vehicleImage.year)}/image/1.jpg${size}`;
};

const getContentServiceStockPhotoData = async (vehicleImage: VehicleImage): Promise<string | null> => {
    const stockPhotoUrl = getContentServiceStockUrl(vehicleImage);
    return await axios({
        method: 'get',
        url: stockPhotoUrl,
        responseType: 'blob',
    })
        .then((response: AxiosResponse<Blob>) => {
            if (stockPhotoUrl === response.request?.responseURL) {
                return window.URL.createObjectURL(response.data);
            }

            return null;
        })
        .catch(() => {
            return null;
        });
};

const getContentServiceStockUrl = (vehicleImage: VehicleImage): string => {
    let angleCode = '089';
    switch (vehicleImage.angle) {
        case 'back':
            angleCode = '119';
            break;
        case 'cargo-empty':
            angleCode = '115';
            break;
        case 'cargo-stuff':
            angleCode = '122';
            break;
        case 'dashboard':
            angleCode = '174';
            break;
        case 'front':
            angleCode = '118';
            break;
        case 'side':
            angleCode = '037';
            break;
        case 'angled-front':
        default:
            // Nothing to change.
            break;
    }

    return `https://content-images.carmax.com/stockimages/${encodeURIComponent(
        vehicleImage.year
    )}/${encodeURIComponent(vehicleImage.make)}/${encodeURIComponent(vehicleImage.model)}/st2400-${angleCode}-evoxweb${vehicleImage.size}.png`;
};

const cleanupParameter = (vehicleImage: VehicleImage): VehicleImage => {
    const cleanVehicleImage: VehicleImage = {
        ...vehicleImage,
    };

    // Replace whitespace with a dash
    cleanVehicleImage.make = vehicleImage.make.replace(/\s+/g, '-').toLowerCase();

    // Replace ampersand with "and"
    cleanVehicleImage.model = vehicleImage.model.replace(/&/g, 'and');

    // Replace whitespace with a dash
    cleanVehicleImage.model = cleanVehicleImage.model.replace(/\s+/g, '-').toLowerCase();

    // Check for value
    if (!vehicleImage.size) {
        // Assume small
        cleanVehicleImage.size = 'small';
    }

    if (!vehicleImage.angle) {
        // Assume Angled Front
        cleanVehicleImage.angle = 'angled-front';
    }

    return cleanVehicleImage;
};

/**
 *
 * @param vehicleImage - Vehicle Information to get StockImage
 * @returns URL for photo or null if there's no stock image
 */
const getVehicleImageData = async (vehicleImage: VehicleImage): Promise<string | null> => {
    if (!vehicleImage || !vehicleImage.make || !vehicleImage.model || !vehicleImage.year) {
        console.warn('Invalid Parameter: Unable to get vehicle image', vehicleImage);
        return null;
    }
    const cleanVehicleImage = cleanupParameter(vehicleImage);
    return getContentServiceStockPhotoData(cleanVehicleImage).then(contentValue => {
        // Leverage IMG2 as callback when angle is `angled-front`
        if (contentValue == null && (!cleanVehicleImage.angle || cleanVehicleImage.angle == 'angled-front')) {
            return getIMG2StockPhotoData(cleanVehicleImage);
        }
        return contentValue;
    });
};

export default getVehicleImageData;
